import type { Secret, SecretValidationError } from "./types";

/**
 * Validates a secret key according to environment variable naming rules
 */
export function validateSecretKey(
  key: string,
  existingKeys: string[],
  currentIndex?: number,
): string | null {
  if (!key.trim()) {
    return "Secret key is required";
  }

  if (!/^[A-Z_][A-Z0-9_]*$/i.test(key)) {
    return "Secret key must start with a letter or underscore and contain only letters, numbers, and underscores";
  }

  // Check for duplicates, excluding the current secret being edited
  const isDuplicate = existingKeys.some(
    (existingKey, index) => existingKey === key && index !== currentIndex,
  );

  if (isDuplicate) {
    return "Secret key already exists";
  }

  return null;
}

/**
 * Validates a secret value
 */
export function validateSecretValue(value: string): string | null {
  if (!value.trim()) {
    return "Secret value is required";
  }
  return null;
}

/**
 * Validates an entire secret object
 */
export function validateSecret(
  secret: Secret,
  allSecrets: Secret[],
  currentIndex?: number,
): SecretValidationError {
  const existingKeys = allSecrets.map((s) => s.key);

  return {
    key: validateSecretKey(secret.key, existingKeys, currentIndex) || undefined,
    value: validateSecretValue(secret.value) || undefined,
  };
}

/**
 * Checks if a secret is valid for saving
 */
export function canSaveSecret(
  secret: Secret,
  allSecrets: Secret[],
  currentIndex?: number,
): boolean {
  const validation = validateSecret(secret, allSecrets, currentIndex);
  return (
    !validation.key &&
    !validation.value &&
    Boolean(secret.key.trim()) &&
    Boolean(secret.value.trim())
  );
}

/**
 * Generates a unique operation key for deduplication
 */
export function createOperationKey(
  secretKey: string,
  operation: string,
): string {
  return `${secretKey}:${operation}`;
}

/**
 * Safely removes empty secrets from the list
 */
export function cleanupEmptySecrets(secrets: Secret[]): Secret[] {
  return secrets.filter((secret) => {
    // Keep existing secrets even if they're empty (user might be editing)
    if (secret.isExisting) {
      return true;
    }

    // Remove new secrets that are completely empty
    return secret.key.trim() || secret.value.trim();
  });
}

/**
 * Creates a new empty secret
 */
export function createEmptySecret(): Secret {
  return {
    id: `new-${Date.now()}-${Math.random().toString(36).slice(2)}`,
    key: "",
    value: "",
    isExisting: false,
  };
}

/**
 * Transforms server secret keys to Secret objects
 */
export function transformServerSecretsToSecrets(
  secretKeys: string[],
): Secret[] {
  return secretKeys.map((key) => ({
    id: `existing-${key}`,
    key,
    value: "",
    isExisting: true,
  }));
}

/**
 * Checks if two secret arrays are equivalent (same keys)
 */
export function areSecretKeysEqual(
  secrets1: Secret[],
  secrets2: Secret[],
): boolean {
  if (secrets1.length !== secrets2.length) {
    return false;
  }

  const keys1 = secrets1.map((s) => s.key).sort();
  const keys2 = secrets2.map((s) => s.key).sort();

  return keys1.every((key, index) => key === keys2[index]);
}

/**
 * Merges server secrets with current form state, preserving user input
 */
export function mergeSecretsWithServerData(
  currentSecrets: Secret[],
  serverSecretKeys: string[],
): Secret[] {
  // Create a map of current secrets for quick lookup
  const currentSecretsMap = new Map(
    currentSecrets.map((secret) => [secret.key, secret]),
  );

  // Start with server secrets, preserving any existing form data
  const mergedSecrets = serverSecretKeys.map((key) => {
    const existingSecret = currentSecretsMap.get(key);
    return existingSecret
      ? {
          ...existingSecret,
          isExisting: true,
          id: existingSecret.id || `existing-${key}`,
        }
      : {
          id: `existing-${key}`,
          key,
          value: "",
          isExisting: true,
        };
  });

  // Add any new secrets that aren't from the server
  const newSecrets = currentSecrets.filter(
    (secret) => !secret.isExisting && !serverSecretKeys.includes(secret.key),
  );

  return [...mergedSecrets, ...newSecrets];
}
